/* @flow */
declare var Intl: any

import i18n from './i18n'

export const roundNumber = (value: number, roundTo: number): number => {
  return Math.round(value / roundTo) * roundTo
}

export type FormatNumberOptionsType = { decimals: ?number }
export const formatNumber = (number: number, options: FormatNumberOptionsType = { decimals: null }): string => {
  const { decimals } = options
  const result = typeof decimals === 'number' ? formatDecimals(number, decimals) : String(number)

  const [splitNumber, splitDecimals] = result.split('.')
  return splitNumber.replace(/(\d)(?=(\d{3})+(\.|$))/g, "$1'") + (splitDecimals ? `.${splitDecimals}` : '')
}

/**
 * Returns a string which represents the given number with given decimal places.
 *
 * Examples:
 * formatDecimals(1000.1231, 3) => '1000.123'
 * formatDecimals(100.9999, 4) => '100.9999'
 * formatDecimals(-10000.1239, 3) => '-10000.124'
 * formatDecimals(-10, 3) => '-10.000'
 *
 * @param {Number} value The number which will be represented
 * @param {Number} decimals The amount of decimal places
 */
export const formatDecimals = (value: number, decimals: number): string => {
  if (decimals == 0) return String(value)
  return roundNumber(value, 1 / Math.pow(10, decimals)).toFixed(decimals)
}

export const parseFormatted = (string: string): number => {
  return parseFloat(String(string).replace(/'/g, ''))
}

export const toCurrency = (value: number, currency: string = 'CHF'): string => {
  const n = parseFloat(value) || 0
  if (window.Intl !== undefined) {
    return new window.Intl.NumberFormat(i18n.localeCountry, {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'code'
    }).format(n)
  } else {
    return `${currency} ${formatNumber(n)}`
  }
}

/**
 * Returns a number whose value is limited to the given range.
 *
 * Example: limit the output of the value to a range between 0 and 255
 * clamp(0, 0, 255)   => 0
 * clamp(128, 0, 255) => 128
 * clamp(255, 0, 255) => 255
 * clamp(-5, 0, 255)  => 0
 * clamp(300, 0, 255) => 255
 *
 * @param {Number} min The lower boundary of the output range
 * @param {Number} max The upper boundary of the output range
 * @return {Number} A number in the range [min, max]
 */
export const clamp = (value: number, min: number, max: number): number => {
  if (min > max) throw Error('min argument greather than max argument')
  return Math.min(Math.max(value, min), max)
}

/**
 * Checks whether a given value is actually a representable number.
 *
 * Examples:
 *
 * @param {*} value The value to check whether it's numeric
 * @return {Boolean} True if it's a representable number
 */
export const isNumeric = (value: any): boolean => {
  return !isNaN(parseFloat(value)) && isFinite(value)
}
